import React from "react";
import { Check } from "lucide-react";
import { useTheme } from "../../../hooks/useTheme.js";
import StepHeader from "./StepHeader";

// Where the policy lives. Same env var and fallback the job-application page
// uses, so both candidate-facing consent surfaces point at one document.
const PRIVACY_POLICY_URL =
  process.env.REACT_APP_PRIVACY_POLICY_URL || "https://geniehire.ai/privacy-policy";

// First screen of the pre-check: the welcome greeting and the consent checkboxes
// share one page. Leaving is offered two ways, because they are different acts:
// Back is "not now", Decline is "no".
const ConsentStep = ({
  candidateName,
  consentItems = [],
  agreements = [],
  setAgreements,
  stepNumber,
  totalSteps,
  onContinue,
  onBack,
  onDecline,
  isSaving = false,
  isDeclining = false,
  errorMessage = null,
  isAuthError = false,
  onGetNewLink,
}) => {
  const { theme } = useTheme();
  const isLight = theme === "light";

  // First name only; falls back to a plain greeting when we have no name.
  const firstName = (candidateName || "").trim().split(/\s+/)[0];
  const heading = firstName ? `Welcome, ${firstName}` : "Welcome";

  const allAgreed =
    consentItems.length > 0 &&
    agreements.length === consentItems.length &&
    agreements.every(Boolean);

  // Any write in flight locks the whole row, so the three actions can't race.
  const busy = isSaving || isDeclining;

  const toggle = (index) => {
    setAgreements((prev) =>
      prev.map((agreed, i) => (i === index ? !agreed : agreed))
    );
  };

  return (
    <div className="w-full">
      <StepHeader
        stepNumber={stepNumber}
        totalSteps={totalSteps}
        title={heading}
        description={`Please read and confirm ${
          consentItems.length === 2 ? "both" : "all"
        } items below. You can withdraw consent at any time by ending the session, before it's submitted for review.`}
      />

      <div
        className={`mt-7 rounded-2xl border ${
          isLight
            ? "bg-white border-[#ECEDF5] shadow-[0_1px_2px_rgba(16,24,40,0.04)]"
            : "bg-gray-800/50 border-gray-700/50 backdrop-blur-sm"
        }`}
      >
        <ul
          className={`px-6 divide-y ${
            isLight ? "divide-[#F1F2F7]" : "divide-gray-700/50"
          }`}
        >
          {consentItems.map((item, index) => {
            const checked = Boolean(agreements[index]);

            return (
              <li key={item.id} className="py-5">
                <label className="flex items-start gap-3.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(index)}
                    className="sr-only"
                  />
                  <span
                    aria-hidden="true"
                    className={`w-[18px] h-[18px] mt-0.5 shrink-0 rounded-[5px] flex items-center justify-center transition-colors ${
                      checked
                        ? "bg-gradient-to-br from-[#2F64FF] to-[#FB54AE]"
                        : isLight
                        ? "bg-[#F1F2F7] border border-[#E1E3EC] group-hover:border-[#C9CCDA]"
                        : "bg-gray-700/60 border border-gray-600 group-hover:border-gray-500"
                    }`}
                  >
                    {checked && (
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    )}
                  </span>

                  <span className="min-w-0">
                    <span
                      className={`block text-[15px] font-normal leading-relaxed ${
                        isLight ? "text-slate-900" : "text-white"
                      }`}
                    >
                      {item.label}
                    </span>
                    <span
                      className={`block text-[13px] leading-relaxed mt-1.5 ${
                        isLight ? "text-slate-400" : "text-gray-400"
                      }`}
                    >
                      {item.description}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      {errorMessage && (
        <div className="mt-5 text-right">
          <p
            role="alert"
            className="text-[12px] leading-relaxed text-red-500 dark:text-red-400"
          >
            {errorMessage}
          </p>
          {isAuthError && (
            <button
              type="button"
              onClick={onGetNewLink}
              className={`mt-1.5 text-[12px] font-medium underline underline-offset-2 ${
                isLight
                  ? "text-slate-600 hover:text-slate-800"
                  : "text-gray-300 hover:text-white"
              }`}
            >
              Get your test link again
            </button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-4 mt-6">
        {/* Leaving, on the left: neither is the action we want, so neither is
            styled to compete with Continue. */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={onBack}
            disabled={busy}
            className={`px-5 py-2.5 rounded-full text-[14px] font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isLight
                ? "border-[#E1E3EC] text-slate-600 hover:bg-[#F6F7FB]"
                : "border-gray-600 text-gray-300 hover:bg-gray-700/40"
            }`}
          >
            Back
          </button>

          <button
            onClick={onDecline}
            disabled={busy}
            className={`px-5 py-2.5 rounded-full text-[14px] font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isLight
                ? "border-red-200 text-red-600 hover:bg-red-50"
                : "border-red-500/40 text-red-400 hover:bg-red-500/10"
            }`}
          >
            {isDeclining ? "Declining…" : "Decline"}
          </button>
        </div>

        <button
          onClick={onContinue}
          disabled={!allAgreed || busy}
          className={`shrink-0 px-7 py-2.5 rounded-full text-[14px] font-medium text-white transition-opacity ${
            allAgreed && !busy
              ? "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] hover:opacity-90"
              : allAgreed
              ? "bg-gradient-to-r from-[#2F64FF] to-[#FB54AE] opacity-60 cursor-wait"
              : "bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400"
          }`}
        >
          {isSaving ? "Saving…" : "I agree, Continue"}
        </button>
      </div>

      <p
        className={`mt-3.5 text-right text-[12px] ${
          isLight ? "text-slate-400" : "text-gray-500"
        }`}
      >
        By continuing you accept our,{" "}
        <a
          href={PRIVACY_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`underline underline-offset-2 font-medium ${
            isLight
              ? "text-slate-500 hover:text-slate-700"
              : "text-gray-400 hover:text-gray-200"
          }`}
        >
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
};

export default ConsentStep;
